import { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import {
  addItemToCart,
  addKitchenIdToCart,
} from "../../store/cart/cart.action";
import { useDispatch, useSelector } from "react-redux";
import { selectCartItems } from "../../store/cart/cart.selector";
import Button, { BUTTON_TYPE_CLASSES } from "../button/button.component";
import {
  getColor,
  getFontName,
  isOnlineOrderConfigValid,
} from "../../utils/utils.common";
import { MenuVariants } from "../menu-variants/menu-variants.component";
import { useEffect } from "react";
import {
  IconContainer,
  DishName,
  DishNameEllips,
  DescriptionText,
  PromoAvailabilityText,
  TextBox,
  DishPrice,
  QuantityContainer,
  ModalContainer,
  ModalScrollContainer,
} from "./menu-item-modal.styles";
import { ImageSkeleton as CardImageSkeleton } from "../menu-item/menu-item.styles.jsx";
import { dishSoldOut } from "../../constants";
import { selectOnlineOrdersConfigs } from "../../store/kitchen/kitchen.selector";
import { logEvent } from "firebase/analytics";
import { analytics } from "../../utils/firebase/firebase.utils";
import Quantity from "../shared/quantity/quantity.component";
import Status from "../shared/status/status.component";
import { CloseIcon } from "../icons";
import Note from "../shared/note/note.component";
import { useMediaQuery } from "@mui/material";
import {
  businessOpenCheck,
  findNextOpening,
} from "../../utils/closedKitchenCheck";
import {
  formatPromoAvailabilityText,
  isDishPromoUnavailable,
} from "../../utils/promoCheck";
import {
  getEffectiveBasePriceOptionNameKey,
  formatDishItemPriceDisplay,
  resolveOptionCharge,
  resolveOptionExtraCharge,
} from "../../utils/variantPricing";

export const TransitionsModal = ({
  product,
  showModal,
  toggleShowModal,
  kitchen,
  onConfirmSelection,
  actionLabel,
  hideQuantityControls = false,
  bundlePriceAdjustment,
}) => {
  const {
    dishName,
    dishDescription,
    dishPrice,
    remoteUri,
    variants,
    dishStatus,
    stockDetails,
  } = product;
  const [quantity, setQuantity] = useState(1);
  const [dishPriceWithVariants, setDishPriceWithVariants] = useState(
    Number(dishPrice)
  );
  const [variantSelected, setVariantSelected] = useState([]);
  const [itemNote, setItemNote] = useState(product?.note || null);
  const [minimumMandatoryVariants, setMinimumMandatoryVariants] = useState(0);
  const [minimumVariantsSelected, setMinimumVariantsSelected] = useState(0);
  const kitchenId = kitchen?.kitchenId;
  const kitchenTier = kitchen?.tier;

  const isMobile = useMediaQuery("(max-width: 768px)");
  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const isKitchenOpen = businessOpenCheck(
    kitchen?.hours?.schedule ?? null,
    kitchen?.timezone
  );
  const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);

  const headerModalBackground = getColor(
    "header-modal-background",
    onlineOrdersConfig
  );
  const brandAccent = getColor("brand-accent", onlineOrdersConfig);
  const backgroundTertiary = getColor(
    "background-tertiary",
    onlineOrdersConfig
  );
  const textBody = getColor("text-body", onlineOrdersConfig);
  const brandTertiary = getColor("brand-tertiary", onlineOrdersConfig);
  const brandPrimary = getColor("brand-primary", onlineOrdersConfig);
  const backgroundSecondary = getColor(
    "background-secondary",
    onlineOrdersConfig
  );
  const brandFontStyle = getFontName("brand-font-style", onlineOrdersConfig);

  const [hasBorder, setHasBorder] = useState(false);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  const findNextOpeningTime = findNextOpening(
    kitchen?.hours?.schedule ?? null,
    kitchen?.timezone
  );
  const skeletonBase = backgroundTertiary;
  const skeletonHighlight =
    headerModalBackground?.toLowerCase?.() === "#000000"
      ? "rgba(255, 255, 255, 0.18)"
      : "rgba(0, 0, 0, 0.06)";

  const contentRef = useRef(null);

  const isDishOutOfStock =
    stockDetails && stockDetails.isEnabled && stockDetails.quantity === 0;

  const soldOut = dishStatus === dishSoldOut || isDishOutOfStock;
  const promoUnavailable = isDishPromoUnavailable(product);
  const cannotOrder = soldOut || promoUnavailable;
  const unavailableButtonLabel = soldOut ? "Sold Out" : "Unavailable";
  const isBundleSelectionFlow = onConfirmSelection && hideQuantityControls;
  const formatPriceAdjustment = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num === 0) return null;
    return num < 0 ? `-$${Math.abs(num).toFixed(2)}` : `+$${num.toFixed(2)}`;
  };
  const formatSignedCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num === 0) return null;
    return num < 0 ? `-$${Math.abs(num).toFixed(2)}` : `+$${num.toFixed(2)}`;
  };
  const adjustmentText = formatPriceAdjustment(bundlePriceAdjustment);
  const bundleDesktopHeightStyle =
    isBundleSelectionFlow && !isMobile
      ? { minHeight: "90%", height: "90%", maxHeight: "90%", overflowX: "hidden" }
      : undefined;
  const handleModalClose = (_, reason) => {
    if (isBundleSelectionFlow && !isMobile && reason === "backdropClick") return;
    toggleShowModal();
  };
  const lockedModifierRules = useMemo(
    () =>
      (Array.isArray(product?._lockedModifiers) ? product._lockedModifiers : []).filter(
        (rule) => (rule?.locked ?? true) === true && Boolean(rule?.variantId)
      ),
    [product]
  );
  const lockedVariantIds = useMemo(
    () => new Set(lockedModifierRules.map((rule) => rule?.variantId).filter(Boolean)),
    [lockedModifierRules]
  );
  const visibleVariants = useMemo(
    () => (Array.isArray(variants) ? variants.filter((variant) => !lockedVariantIds.has(variant?.id)) : []),
    [variants, lockedVariantIds]
  );
  const promoAvailabilityText = useMemo(
    () => formatPromoAvailabilityText(product?.promo),
    [product?.promo]
  );

  const buildLockedVariants = () => {
    if (!Array.isArray(variants) || lockedModifierRules.length === 0) return [];

    return lockedModifierRules
      .map((rule) => {
        // Hide-only lock: variant remains locked/hidden, no preselected option.
        if (!rule?.optionName) return null;

        const variant = variants.find((v) => v?.id === rule?.variantId);
        const option = variant?.options?.find((opt) => opt?.name === rule?.optionName);
        if (!variant || !option) return null;

        return {
          ...option,
          charge: "0",
          extraCharge: false,
          isLocked: true,
          variantId: variant.id,
          optionCategory: variant.variantName,
          mandatoryChoice: false,
        };
      })
      .filter(Boolean);
  };

  const getVariantChargeTotal = (selected) =>
    (selected || []).reduce((sum, item) => {
      if (item?.isLocked) return sum;
      if (item?.extraCharge && item?.charge) {
        return sum + Number(item.charge || 0);
      }
      return sum;
    }, 0);

  const bundleButtonDelta = isBundleSelectionFlow
    ? Number(bundlePriceAdjustment || 0) + getVariantChargeTotal(variantSelected) * quantity
    : null;
  const bundleButtonDeltaText = formatSignedCurrency(bundleButtonDelta);

  useEffect(() => {
    const timer = setTimeout(() => {
      const contentEl = contentRef.current;
      if (!contentEl) return;

      const handleScroll = () => {
        if (remoteUri) {
          setHasBorder(contentEl.scrollTop > 328);
        } else {
          setHasBorder(contentEl.scrollTop > 0);
        }
      };
      contentEl.addEventListener("scroll", handleScroll);
      handleScroll();

      return () => contentEl.removeEventListener("scroll", handleScroll);
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setHeroImageLoaded(false);
  }, [remoteUri, showModal]);

  useEffect(() => {
    const lockedVariants = buildLockedVariants();
    const existingVariants = Array.isArray(product?.variantSelected)
      ? product.variantSelected.filter(
          (item) => !(item?.variantId && lockedVariantIds.has(item.variantId)) && !item?.isLocked
        )
      : [];

    const initialVariants = [...lockedVariants, ...existingVariants];
    setVariantSelected(initialVariants);
    setDishPriceWithVariants(Number(dishPrice) + getVariantChargeTotal(existingVariants));
    setErrorStates({});
    setItemNote(product?.note || null);
  }, [product, dishPrice, lockedVariantIds]);

  // Base-price option key for conditional charges: selected base, else item
  // default when there is no base modifier. Null while a base exists but is
  // unselected so conditional extras stay hidden.
  const basePriceOptionNameKey = useMemo(
    () =>
      getEffectiveBasePriceOptionNameKey(
        variants,
        variantSelected,
        product?.defaultConditionalBaseOptionKey
      ),
    [variants, variantSelected, product?.defaultConditionalBaseOptionKey]
  );

  // When the selected base-price option changes, re-price any already-selected
  // conditional pricing modifier options and recompute the running total.
  useEffect(() => {
    setVariantSelected((prev) => {
      const next = (prev || []).map((item) => {
        if (item?.isLocked) return item;
        const group = (variants || []).find(
          (variant) => variant?.variantName === item?.optionCategory
        );
        if (!group?.isConditionalPricingModifier) return item;
        const charge = resolveOptionCharge(item, group, basePriceOptionNameKey);
        return {
          ...item,
          charge,
          extraCharge: resolveOptionExtraCharge(charge),
        };
      });
      setDishPriceWithVariants(
        Number(dishPrice) + getVariantChargeTotal(next)
      );
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePriceOptionNameKey]);

  const [errorStates, setErrorStates] = useState({});

  const subtitleRefs = useRef([]);

  const addProductToCart = () => {
    if (minimumVariantsSelected < minimumMandatoryVariants) {
      const newErrorStates = {};
      visibleVariants?.forEach((variant, idx) => {
        if (variant.mandatoryChoice) {
          const selectedForVariant = variantSelected.filter(
            (item) => item.optionCategory === variant.variantName
          );
          if (selectedForVariant.length < variant.minCounter) {
            newErrorStates[idx] = true;
          }
        }
      });
      setErrorStates(newErrorStates);

      const firstErrorIndex = Object.keys(newErrorStates)[0];
      const firstErrorRef = subtitleRefs.current[firstErrorIndex];

      if (firstErrorRef && contentRef.current) {
        const scrollOffset =
          firstErrorRef.offsetTop - contentRef.current.offsetTop - 72;
        contentRef.current.scrollTo({
          top: scrollOffset,
          behavior: "smooth",
        });
      }

      return;
    }

    const finalVariantSelected = [
      ...buildLockedVariants(),
      ...variantSelected.filter(
        (item) => !(item?.variantId && lockedVariantIds.has(item.variantId)) && !item?.isLocked
      ),
    ];
    if (process.env.NODE_ENV !== "production") {
      console.log("[variants][before-add]", {
        dishName,
        quantity,
        finalVariantSelected,
      });
    }

    if (onConfirmSelection) {
      onConfirmSelection({
        product,
        quantity,
        variantSelected: finalVariantSelected,
        note: itemNote || null,
        dishPrice: Number(dishPrice),
        dishPriceWithVariants,
      });
      toggleShowModal();
      return;
    }

    dispatch(
      addItemToCart(
        cartItems,
        product,
        quantity,
        finalVariantSelected,
        dishPrice,
        dishPriceWithVariants,
        itemNote || null
      )
    );
    dispatch(addKitchenIdToCart(kitchenId));
    toggleShowModal();

    // log add to cart event
    logEvent(analytics, "add_to_cart", {
      kitchenName: kitchen?.kitchenName,
    });
  };

  const increment = () => {
    setQuantity(quantity + 1);
  };
  const decrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  useEffect(() => {
    let count = 0;
    // eslint-disable-next-line array-callback-return
    visibleVariants?.map((variant) => {
      if (variant.mandatoryChoice) {
        count += variant.minCounter;
      }
    });
    setMinimumMandatoryVariants(count);
  }, [visibleVariants]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={showModal}
      onClose={handleModalClose}
      closeAfterTransition
    >
      <Fade in={showModal}>
        <ModalContainer
          headerModalBackground={headerModalBackground}
          isMobile={isMobile}
          style={bundleDesktopHeightStyle}
        >
          <ModalScrollContainer ref={contentRef}>
            <Box style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Box sx={{ position: "sticky", right: 0, top: 0, zIndex: 13 }}>
                <IconContainer
                  onClick={toggleShowModal}
                  headerModalBackground={headerModalBackground}
                  brandPrimary={brandPrimary}
                >
                  <CloseIcon size={"middle"} />
                </IconContainer>
              </Box>
              {remoteUri && (
                <Box
                  sx={{
                    width: "100%",
                    height: "328px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <CardImageSkeleton
                    hidden={heroImageLoaded}
                    skeletonBase={skeletonBase}
                    skeletonHighlight={skeletonHighlight}
                  />
                  <img
                    src={remoteUri}
                    alt=""
                    loading="eager"
                    decoding="async"
                    onLoad={() => setHeroImageLoaded(true)}
                    onError={() => setHeroImageLoaded(true)}
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "328px",
                      display: "block",
                      opacity: heroImageLoaded ? 1 : 0,
                      transition: "opacity 220ms ease",
                    }}
                  />
                </Box>
              )}
              <DishName
                hasBorder={hasBorder}
                brandFontStyle={brandFontStyle}
                brandAccent={brandAccent}
                headerModalBackground={headerModalBackground}
                backgroundTertiary={backgroundTertiary}
              >
                <DishNameEllips hasBorder={hasBorder}>
                  {dishName}
                </DishNameEllips>
              </DishName>
              <TextBox
                backgroundTertiary={backgroundTertiary}
                headerModalBackground={headerModalBackground}
              >
                {promoAvailabilityText && (
                  <PromoAvailabilityText brandTertiary={brandTertiary}>
                    {promoAvailabilityText}
                  </PromoAvailabilityText>
                )}
                {dishDescription && (
                  <DescriptionText textBody={textBody}>
                    {dishDescription?.replaceAll("\\n", "\n")}
                  </DescriptionText>
                )}
                {(!isBundleSelectionFlow || adjustmentText) && (
                  <DishPrice textBody={textBody}>
                    {isBundleSelectionFlow
                      ? adjustmentText
                      : formatDishItemPriceDisplay(dishPrice, variants)}
                  </DishPrice>
                )}
              </TextBox>

              <Box sx={{ paddingBottom: 2 }}>
                {visibleVariants?.map(
                  (
                    {
                      options,
                      variantName: optionCategory,
                      minCounter,
                      maxCounter,
                      mandatoryChoice,
                      isConditionalPricingModifier,
                      isBasePriceModifier,
                    },
                    index
                  ) => {
                    return (
                      <MenuVariants
                        key={index}
                        setDishPriceVariant={setDishPriceWithVariants}
                        setVariantSelected={setVariantSelected}
                        variantSelected={variantSelected}
                        dishPriceVariant={dishPriceWithVariants}
                        setMinimumVariantsSelected={setMinimumVariantsSelected}
                        maxCounter={maxCounter}
                        minCounter={minCounter}
                        optionCategory={optionCategory}
                        optionNumber={index}
                        options={options}
                        mandatoryChoice={mandatoryChoice}
                        kitchenTier={kitchenTier}
                        brandTertiary={brandTertiary}
                        textBody={textBody}
                        backgroundTertiary={backgroundTertiary}
                        headerModalBackground={headerModalBackground}
                        backgroundSecondary={backgroundSecondary}
                        errorState={!!errorStates[index]}
                        isConditionalPricingModifier={!!isConditionalPricingModifier}
                        isBasePriceModifier={!!isBasePriceModifier}
                        basePriceOptionNameKey={basePriceOptionNameKey}
                        onClearError={() => {
                          setErrorStates((prev) => ({
                            ...prev,
                            [index]: false,
                          }));
                        }}
                        ref={(el) => (subtitleRefs.current[index] = el)}
                      />
                    );
                  }
                )}
              </Box>
              {isOnlineOrderConfigValid(onlineOrdersConfig) && (
                <Box
                  sx={{
                    padding: "16px 24px 32px",
                    "@media screen and (max-width: 768px)": {
                      padding: "8px 16px 32px",
                    },
                  }}
                >
                  <Note
                    title="Item Note"
                    buttonText="Add Item Note"
                    defaultValue={itemNote || ""}
                    onBlurAction={(value) => setItemNote(value || null)}
                    onClearAction={() => setItemNote(null)}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                borderTopWidth: "1px",
                borderTopStyle: "solid",
                borderTopColor: backgroundSecondary,
                position: "sticky",
                zIndex: 14,
                bottom: 0,
                left: 0,
                right: 0,
                background: headerModalBackground,
                padding: "20px 24px",
                minHeight: "89px",
                "@media screen and (max-width: 768px)": {
                  minHeight: "85px",
                  padding: "20px 16px",
                },
              }}
            >
              {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                isKitchenOpen &&
                onlineOrdersConfig?.onlineOrderingPaused === false &&
                !cannotOrder && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {!hideQuantityControls && (
                        <QuantityContainer>
                          <Quantity
                            decrement={decrement}
                            increment={increment}
                            label={quantity}
                            isRemoveDisabled={quantity == 1}
                            size={isMobile ? "small" : "medium"}
                          />
                        </QuantityContainer>
                      )}
                      <Button
                        onClick={addProductToCart}
                        buttonType={BUTTON_TYPE_CLASSES.base}
                      >
                        {(actionLabel || "Add to Cart")}
                        {isBundleSelectionFlow
                          ? bundleButtonDeltaText
                            ? ` (${bundleButtonDeltaText})`
                            : ""
                          : Number(dishPriceWithVariants * quantity) !== 0
                            ? ` ($${(dishPriceWithVariants * quantity).toFixed(2)})`
                            : ""}
                      </Button>
                    </Box>
                  </>
                )}
              {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                isKitchenOpen &&
                onlineOrdersConfig?.onlineOrderingPaused === false &&
                cannotOrder && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <QuantityContainer>
                      <Quantity
                        isRemoveDisabled={true}
                        isAddDisabled={true}
                        label={quantity}
                        size={isMobile ? "small" : "medium"}
                      />
                    </QuantityContainer>
                    <Button
                      disabled={true}
                      buttonType={BUTTON_TYPE_CLASSES.base}
                    >
                      {unavailableButtonLabel} (${dishPriceWithVariants})
                    </Button>
                  </Box>
                )}

              {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                !isKitchenOpen && (
                  <Status
                    status="close"
                    primaryText="Closed"
                    secondaryText={`Opens ${findNextOpeningTime?.day} at ${findNextOpeningTime?.time}`}
                    forModalStatus={true}
                  />
                )}

              {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                onlineOrdersConfig?.onlineOrderingPaused &&
                isKitchenOpen && (
                  <Status
                    status="notCurrently"
                    primaryText="Currently not accepting online orders."
                    hasBanner={!!onlineOrdersConfig?.bannerImageURL}
                  />
                )}

              {!isOnlineOrderConfigValid(onlineOrdersConfig) && (
                <Status
                  status="visual"
                  primaryText="Visual Menu Only"
                  forModalStatus={true}
                  statusCircle={false}
                />
              )}
            </Box>
          </ModalScrollContainer>
        </ModalContainer>
      </Fade>
    </Modal>
  );
};
